// React
import React, { useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Components
import BPicker from 'ui/blocks/Picker'
import Popover from 'ui/components/Popover'
import PortalWrapper from 'ui/components/PortalWrapper'
import { Content } from './Content'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { useTimeOptions } from './utils'
import { Format as FormatUtils } from 'back_office/v2/utils/utils'
import moment from 'shared/lib/moment'

export const TimeButton = (props) => {
  const [active, setActive] = useState(false)

  const picker = usePicker()

  useEffect(() => {
    if (!props.usePortal) return

    const isActive = picker.selection === props.mode

    setActive(isActive)

    props.onTrigger?.()

    if (isActive) {
      props.onOpen?.()
    } else {
      props.onHide?.()
    }
  }, [picker.selection, props.mode, props.usePortal])

  const options = useTimeOptions(props)
  const value = props.field.value ?
    moment(props.field.value, 'HH:mm').format(FormatUtils.dateFormat('time')) :
    props.placeholder

  const handleSelect = (value) => {
    props.field.onChange(value)

    setActive(false)
  }

  const handleClick = (e) => {
    e.preventDefault()

    // Altering the callbacks as the active will be set to alter value
    if (active) {
      picker.setSelection(null)

      props.onHide?.()
    } else {
      picker.setSelection(props.mode)

      props.onOpen?.()
    }

    setActive((prev) => !prev)

    props.onTrigger?.()
  }

  const handleTrigger = () => {
    setActive(true)

    props.onTrigger?.()
  }

  const handleHide = () => {
    setActive(false)

    props.onHide?.()
  }

  const handleOpen = () => {
    setActive(true)

    picker.setSelection(props.mode)

    props.onOpen?.()
  }

  const handleSetActive = (e) => {
    e.preventDefault()

    setActive(true)
  }

  if (props.usePortal) {
    return (
      <Fragment>
        <BPicker.Button
          type="button"
          active={active}
          empty={!props.field.value}
          disabled={props.disabled}
          data-value={props['data-value']}
          data-active={String(active)}
          onClick={handleClick}
          mobile={props.mobile}
          error={props.error}
        >
          {value}
        </BPicker.Button>
        <PortalWrapper open={active} containerRef={props.portalRef}>
          <Content
            {...props}
            options={options}
            onSelect={handleSelect}
            showAvailability={props.showAvailability && !!props.lineCount}
            data-tid="Time popover"
          />
        </PortalWrapper>
      </Fragment>
    )
  }

  return (
    <Popover
      {...(props.popoverOptions || {})}
      innerRef={props.innerRef}
      content={(
        <Content
          {...props}
          options={options}
          onSelect={handleSelect}
          showAvailability={props.showAvailability && !!props.lineCount}
          data-tid="Time popover"
        />
      )}
      method="click"
      direction="bottom"
      offset={15}
      onTrigger={handleTrigger}
      onHide={handleHide}
      onOpen={handleOpen}
      delay={[200, null]}
    >
      <BPicker.Button
        type="button"
        active={active}
        empty={!props.field.value}
        disabled={props.disabled}
        data-value={props['data-value']}
        data-active={String(active)}
        error={props.error}
        onClick={handleSetActive}
      >
        {value}
      </BPicker.Button>
    </Popover>
  )
}

TimeButton.propTypes = {
  'field': PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  }),
  'error': PropTypes.bool,
  'popoverOptions': PropTypes.object,
  'inline': PropTypes.bool,
  'mode': PropTypes.string,
  'onSelectionChange': PropTypes.func,
  'disabled': PropTypes.bool,
  'useAMOrPM': PropTypes.bool,
  'showAvailability': PropTypes.oneOf([true, false, 'count']),
  'lineCount': PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  'placeholder': PropTypes.any,
  'onOpen': PropTypes.func,
  'onTrigger': PropTypes.func,
  'onHide': PropTypes.func,
  'data-tid': PropTypes.string,
  'data-value': PropTypes.string,
  'usePortal': PropTypes.bool,
  'mobile': PropTypes.bool,
  'innerRef': PropTypes.func,
  'portalRef': PropTypes.any
}
